import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useRole } from '@/contexts/RoleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCompetencies } from '@/hooks/useCompetencies';
import { usePrograms } from '@/hooks/usePrograms';
import { useContentBank } from '@/hooks/useContent';
import { useContentCategories } from '@/hooks/useContentCategories';
import { ContentPlayerModal } from '@/components/ContentPlayerModal';
import { CONTENT_TYPE_MAP } from '@/lib/contentTypeMap';
import {
  useProgramDetail,
  useUpsertProgram,
  useDeleteProgram,
  useDuplicateProgram,
  useSaveProgramWorkshops,
  useSaveProgramChapters,
  useSaveProgramCertificate,
  useDeleteProgramCertificate,
  useProgramCertificates,
  useEnsurePerpetualCohort,
} from '@/hooks/useProgramManagement';
import { emptyDiploma, type DiplomaConfig, useCertificateConfig } from '@/hooks/useCertificateConfig';
import { ProgramCertificateTab } from '@/components/admin/ProgramCertificateTab';
import { RouteInterestsTab } from '@/components/admin/RouteInterestsTab';
import {
  AlertCircle, Plus, Trash2, GraduationCap, Pencil, ArrowLeft, Save, Copy,
  Calendar, Users, Clock, BookOpen, Video, Award, Upload, ChevronLeft,
  ChevronRight, Palette, FileText, Layers, Eye, GripVertical, Search, Loader2, Star,
  Check, X,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useAIToolsCatalog } from '@/hooks/useAIToolsCatalog';

// ── Types (local editor state) ──

// Selector buscable de herramientas: las badges se construyen SIEMPRE desde lo
// guardado en el taller (selected), nunca filtrando contra el catálogo. El
// catálogo (ai_tools_catalog) solo alimenta el buscador de "Añadir herramienta".
function ToolPicker({ selected, onChange, catalogNames, catalogLoading }: {
  selected: string[];
  onChange: (tools: string[]) => void;
  catalogNames: string[];
  catalogLoading: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {selected.map(tool => (
        <Badge key={tool} variant="default" className="bg-primary text-xs gap-1">
          {tool}
          <button
            type="button"
            aria-label={`Quitar ${tool}`}
            className="ml-0.5 hover:text-destructive-foreground"
            onClick={() => onChange(selected.filter(t => t !== tool))}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="h-6 text-xs" disabled={catalogLoading}>
            <Plus className="h-3 w-3 mr-1" /> Añadir herramienta
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-72" align="start">
          <Command>
            <CommandInput placeholder="Buscar herramienta..." />
            <CommandList>
              <CommandEmpty>Sin resultados en el catálogo</CommandEmpty>
              <CommandGroup>
                {catalogNames.map(name => {
                  const already = selected.includes(name);
                  return (
                    <CommandItem
                      key={name}
                      value={name}
                      onSelect={() => {
                        if (!already) onChange([...selected, name]);
                        setOpen(false);
                      }}
                    >
                      <Check className={cn("mr-2 h-3 w-3", already ? "opacity-100" : "opacity-0")} />
                      {name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface Workshop {
  id: string;
  weekNumber: number;
  title: string;
  description: string;
  duration: string;
  materialLink: string;
  problemaConcreto: string;
  resultadoEsperado: string;
  tiempoEstimado: string;
  tipAdicional: string;
  deliverableTools: string[];
  requiresDeliverable: boolean;
  deliverableResourceUrl: string | null;
}

interface ProgramContent {
  id: string;
  contentId: string;
  name: string;
  type: string;
  duration: string;
  mandatory: boolean;
  order: number;
}

type UnlockCondition = 'diagnostic' | 'has_project';

interface ProgramChapter {
  id: string;
  name: string;
  weekNumber: number;
  order: number;
  unlockConditions: UnlockCondition[];
  contents: ProgramContent[];
}

interface CertificateConfig {
  images: string[];
  publicName: string;
  privateName: string;
  description: string;
  additionalUrl: string;
  skills: { id: string; name: string }[];
  criteria: { id: string; type: string; description: string; url: string }[];
  attributes: {
    type: string;
    level: string;
    timeUnit: string;
    timeAmount: number;
    cost: string;
  };
}

interface EditorProgram {
  id: string;
  name: string;
  description: string;
  level: number;
  category: 'general' | 'specialized';
  duration: string;
  imageUrl: string;
  
  skills: string[];
  color: string;
  status: 'active' | 'public' | 'draft' | 'archived';
  modality: 'cohort' | 'self_paced';
  maxParticipants: number;
  isFeatured: boolean;
  sequentialUnlock: boolean;
  workshops: Workshop[];
  /** La ruta solo define si contempla mentorías; cada cohorte crea las suyas. */
  hasMentorships: boolean;
  chapters: ProgramChapter[];
}

// ── Aviso de temario desfasado (rutas de autoaprendizaje) ──
// Un inscrito "conserva el temario anterior" cuando su snapshot personal difiere
// del temario vivo de la ruta. Quien no tiene snapshot usa el temario vivo.
function SelfPacedEnrolleeNotice({ programId }: { programId: string }) {
  const { data: staleCount, isLoading } = useQuery({
    queryKey: ['self-paced-stale-enrollees', programId],
    enabled: !!programId,
    queryFn: async (): Promise<number> => {
      const { data: perpetualCohorts, error: cohErr } = await supabase
        .from('cohorts')
        .select('id')
        .eq('program_id', programId)
        .eq('is_perpetual', true);
      if (cohErr) throw cohErr;
      const cohortIds = (perpetualCohorts ?? []).map((c: { id: string }) => c.id);
      if (!cohortIds.length) return 0;

      const { data: chapters, error: chErr } = await supabase
        .from('program_chapters')
        .select('id')
        .eq('program_id', programId);
      if (chErr) throw chErr;
      const chapterIds = (chapters ?? []).map((c: { id: string }) => c.id);

      const liveMap = new Map<string, boolean>();
      if (chapterIds.length) {
        const { data: links, error: lkErr } = await supabase
          .from('chapter_contents')
          .select('content_id, is_mandatory')
          .in('chapter_id', chapterIds);
        if (lkErr) throw lkErr;
        for (const l of (links ?? []) as { content_id: string; is_mandatory: boolean | null }[]) {
          liveMap.set(l.content_id, !!l.is_mandatory);
        }
      }
      const liveKey = Array.from(liveMap.entries()).sort().map(([k, v]) => `${k}:${v}`).join('|');

      const { data: snapRows, error: snapErr } = await supabase
        .from('self_paced_required_content')
        .select('user_id, content_id, is_mandatory')
        .in('cohort_id', cohortIds);
      if (snapErr) throw snapErr;

      const byUser = new Map<string, Map<string, boolean>>();
      for (const r of (snapRows ?? []) as { user_id: string; content_id: string; is_mandatory: boolean }[]) {
        if (!byUser.has(r.user_id)) byUser.set(r.user_id, new Map());
        byUser.get(r.user_id)!.set(r.content_id, !!r.is_mandatory);
      }

      let stale = 0;
      for (const snap of byUser.values()) {
        const key = Array.from(snap.entries()).sort().map(([k, v]) => `${k}:${v}`).join('|');
        if (key !== liveKey) stale += 1;
      }
      return stale;
    },
  });

  if (isLoading || !staleCount) return null;

  return (
    <p className="text-xs text-amber-600 dark:text-amber-500">
      {staleCount} {staleCount === 1 ? 'inscrito conserva' : 'inscritos conservan'} el temario anterior.
    </p>
  );
}

// ── Constants ──

const statusOptions = [
  { value: 'active', label: 'Activo' },
  { value: 'public', label: 'Público' },
  { value: 'draft', label: 'Borrador' },
  { value: 'archived', label: 'Archivado' },
];

const certificateTypes = ['Certificación', 'Insignia', 'Diploma', 'Credencial'];
const levelOptions = ['Básico', 'Intermedio', 'Avanzado', 'Experto'];
const timeUnits = ['Horas', 'Días', 'Semanas', 'Meses'];
const costOptions = ['Gratuito', 'Pago', 'Incluido en suscripción'];
const criterionTypes = ['Título', 'Competencia', 'Proyecto', 'Evaluación', 'Asistencia', 'Otro'];

// skillOptions now loaded from DB via useCompetencies

const unlockConditionLabels: Record<UnlockCondition, string> = {
  diagnostic: 'Completar diagnóstico',
  has_project: 'Tener al menos un proyecto',
};

/** DB programs.status is entity_status; editor uses active | public | draft | archived */
function dbStatusToEditor(status: string | null | undefined): EditorProgram['status'] {
  const map: Record<string, EditorProgram['status']> = {
    active: 'active',
    public: 'public',
    inactive: 'draft',
    draft: 'draft',
    suspended: 'archived',
  };
  return map[status || ''] || 'draft';
}

// ── Helper: Map DB detail to editor state ──
function mapDbToEditor(detail: any): EditorProgram {
  const p = detail.program;
  return {
    id: p.id,
    name: p.name || '',
    description: p.description || '',
    level: p.level || 1,
    category: (p.category as any) || 'general',
    duration: p.duration || '',
    imageUrl: (p as any).image_url || '',
    
    skills: p.skills || [],
    color: p.color || 'from-primary to-primary/70',
    status: dbStatusToEditor(p.status),
    modality: (p.modality as EditorProgram['modality']) || 'cohort',
    maxParticipants: p.max_participants || 30,
    isFeatured: (p as any).is_featured ?? false,
    sequentialUnlock: p.sequential_unlock ?? false,
    workshops: (detail.workshops || []).map((ws: any) => ({
      id: ws.id,
      weekNumber: ws.week_number || 1,
      title: ws.title || '',
      description: ws.description || '',
      duration: ws.duration || '',
      materialLink: ws.material_link || '',
      problemaConcreto: ws.problema_concreto || '',
      resultadoEsperado: ws.resultado_esperado || '',
      tiempoEstimado: ws.tiempo_estimado || '',
      tipAdicional: ws.tip_adicional || '',
      deliverableTools: (ws.tools || []).map((t: any) => t.tool_name),
      requiresDeliverable: ws.requires_deliverable ?? true,
      deliverableResourceUrl: ws.deliverable_resource_url ?? null,
    })),
    hasMentorships: (p as any).has_mentorships ?? (detail.mentorships || []).length > 0,
    chapters: (detail.chapters || []).map((ch: any) => ({
      id: ch.id,
      name: ch.name || '',
      weekNumber: ch.week_number || 1,
      order: ch.sort_order || 1,
      unlockConditions: (ch.unlock_conditions || []) as UnlockCondition[],
      contents: (ch.contents || []).map((cc: any) => ({
        id: cc.id,
        contentId: cc.content_id,
        name: cc.content_bank?.name || 'Sin nombre',
        type: cc.content_bank?.content_type || '',
        duration: cc.content_bank?.duration || '',
        mandatory: cc.is_mandatory ?? false,
        order: cc.sort_order || 0,
      })),
    })),
  };
}

// ── Component ──

export default function ProgramManagementPage() {
  const { currentRole } = useRole();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  // DB hooks
  const { data: dbPrograms, isLoading: loadingPrograms } = usePrograms();
  const contentBankQuery = useContentBank();
  const contentBankItems = contentBankQuery.data || [];
  const upsertProgram = useUpsertProgram();
  const ensurePerpetualCohort = useEnsurePerpetualCohort();
  const deleteProgram = useDeleteProgram();
  const duplicateProgram = useDuplicateProgram();
  const saveWorkshops = useSaveProgramWorkshops();
  const saveChapters = useSaveProgramChapters();
  const saveCertificate = useSaveProgramCertificate();
  const { data: competenciesData = [] } = useCompetencies();
  const skillOptions = competenciesData.map(c => c.name);

  // Real data: organizations, cohorts, certificate logs
  const { data: dbOrganizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('id, name').order('name');
      if (error) throw error;
      return data || [];
    },
  });
  const organizations = dbOrganizations || [];

  const { data: dbCohorts } = useQuery({
    queryKey: ['cohorts-all'],
    queryFn: async () => {
      const { data, error } = await supabase.from('cohorts').select('id, name, organization_id').order('name');
      if (error) throw error;
      return data || [];
    },
  });
  const allCohorts = dbCohorts || [];

  // Editor state
  const [editingProgramId, setEditingProgramId] = useState<string | null>(null);
  const [editingProgram, setEditingProgram] = useState<EditorProgram | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [previewResource, setPreviewResource] = useState<{ url: string; title: string } | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  useEffect(() => {
    if (!previewResource?.url) {
      setPreviewHtml(null);
      return;
    }
    let cancelled = false;
    setPreviewHtml(null);
    fetch(previewResource.url)
      .then((r) => r.text())
      .then((html) => { if (!cancelled) setPreviewHtml(html); })
      .catch(() => { if (!cancelled) setPreviewHtml('<p style="font-family:sans-serif;padding:24px">No se pudo cargar el recurso.</p>'); });
    return () => { cancelled = true; };
  }, [previewResource?.url]);
  const [activeTab, setActiveTab] = useState('info');
  const [saving, setSaving] = useState(false);

  // ── Filtros del listado de rutas (deben ir antes de cualquier return condicional) ──
  const [listSearch, setListSearch] = useState('');
  const [listModality, setListModality] = useState<'all' | 'cohort' | 'self_paced'>('all');
  const [listCategory, setListCategory] = useState<'all' | 'general' | 'specialized'>('all');
  const [listStatus, setListStatus] = useState<'all' | 'active' | 'public' | 'draft' | 'archived'>('all');

  // Certificate
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSkill, setSelectedSkill] = useState('');

  // Chapter content state
  const [addingContentToChapter, setAddingContentToChapter] = useState<string | null>(null);
  const [contentBankSearch, setContentBankSearch] = useState('');
  const [contentBankStatusFilter, setContentBankStatusFilter] = useState<string>('published');
  const [contentBankCategoryFilter, setContentBankCategoryFilter] = useState<string>('all');
  const [previewBankItem, setPreviewBankItem] = useState<any | null>(null);

  // Load detail when editing
  const detailQuery = useProgramDetail(editingProgramId);

  // Track which programId we already hydrated into local state, to prevent
  // background refetches from overwriting unsaved local edits (e.g. removed workshops).
  const hydratedForIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editingProgramId) {
      hydratedForIdRef.current = null;
      return;
    }
    if (
      detailQuery.data &&
      !isCreating &&
      hydratedForIdRef.current !== editingProgramId
    ) {
      setEditingProgram(mapDbToEditor(detailQuery.data));
      hydratedForIdRef.current = editingProgramId;
    }
  }, [detailQuery.data, editingProgramId, isCreating]);

  // Hooks declarados antes de cualquier return temprano (evita error de hooks al cambiar de rol).
  const { data: aiToolsCatalog, isLoading: aiToolsLoading } = useAIToolsCatalog();
  const { data: contentCategories = [] } = useContentCategories();

  if (currentRole !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-warning mx-auto mb-4" />
          <p className="text-lg font-medium">Acceso restringido</p>
          <p className="text-muted-foreground">Solo super administradores pueden acceder a esta sección.</p>
        </Card>
      </div>
    );
  }

  // ── Handlers ──

  const handleEdit = (programId: string) => {
    setEditingProgramId(programId);
    setIsCreating(false);
    setActiveTab('info');
  };

  const handleCreateNew = () => {
    const newProgram: EditorProgram = {
      id: '',
      name: '',
      description: '',
      level: 1,
      category: 'general',
      duration: '30 horas',
      imageUrl: '',
      
      skills: [],
      color: 'from-primary to-primary/70',
      status: 'draft',
      modality: 'cohort',
      maxParticipants: 30,
      isFeatured: false,
      sequentialUnlock: false,
      workshops: [],
      hasMentorships: false,
      chapters: [],
    };
    setEditingProgram(newProgram);
    setEditingProgramId(null);
    setIsCreating(true);
    setActiveTab('info');
  };

  const handleDuplicate = async (programId: string) => {
    try {
      await duplicateProgram.mutateAsync(programId);
      toast({ title: "Ruta duplicado", description: "Se ha creado una copia de la ruta" });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProgram.mutateAsync(id);
      setShowDeleteDialog(null);
      toast({ title: "Ruta eliminado", description: "La ruta ha sido eliminado correctamente" });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleSave = async () => {
    if (!editingProgram?.name) {
      toast({ title: "Error", description: "El nombre de la ruta es obligatorio", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      // 1. Save program
      const statusMap: Record<string, string> = {
        active: 'active',
        public: 'public',
        draft: 'draft',
        archived: 'suspended',
      };
      const savedProgram = await upsertProgram.mutateAsync({
        id: isCreating ? undefined : editingProgram.id,
        name: editingProgram.name,
        description: editingProgram.description || null,
        level: editingProgram.level,
        category: editingProgram.category,
        duration: editingProgram.duration || null,
        image_url: editingProgram.imageUrl || null,
        
        skills: editingProgram.skills.length > 0 ? editingProgram.skills : null,
        color: editingProgram.color || null,
        status: (statusMap[editingProgram.status] || 'active') as any,
        max_participants: editingProgram.modality === 'self_paced' ? null : editingProgram.maxParticipants,
        sequential_unlock: editingProgram.sequentialUnlock,
        modality: editingProgram.modality,
        is_featured: editingProgram.isFeatured,
        has_mentorships: editingProgram.modality === 'self_paced' ? false : editingProgram.hasMentorships,
      });

      const programId = savedProgram.id;

      // Ruta de autoaprendizaje publicada → asegurar su cohorte perpetua (idempotente)
      if (editingProgram.modality === 'self_paced' && ['active', 'public'].includes(editingProgram.status)) {
        try {
          await ensurePerpetualCohort.mutateAsync({ programId, programName: editingProgram.name });
        } catch (e: any) {
          toast({ title: 'Aviso', description: `No se pudo preparar la inscripción continua: ${e.message}`, variant: 'destructive' });
        }
      }

      // 2. Save sub-entities in parallel
      await Promise.all([
        saveWorkshops.mutateAsync({
          programId,
          workshops: editingProgram.workshops.map((ws, i) => ({
            id: ws.id,
            title: ws.title,
            description: ws.description,
            duration: ws.duration,
            week_number: ws.weekNumber,
            sort_order: i + 1,
            material_link: ws.materialLink,
            problema_concreto: ws.problemaConcreto,
            resultado_esperado: ws.resultadoEsperado,
            tiempo_estimado: ws.tiempoEstimado,
            tip_adicional: ws.tipAdicional,
            tools: ws.deliverableTools,
            requires_deliverable: ws.requiresDeliverable,
            deliverable_resource_url: ws.deliverableResourceUrl ?? null,
          })),
        }),
        saveChapters.mutateAsync({
          programId,
          chapters: editingProgram.chapters.sort((a, b) => a.order - b.order).map((ch, i) => ({
            name: ch.name,
            week_number: ch.weekNumber,
            sort_order: i + 1,
            unlock_conditions: ch.unlockConditions,
            contents: ch.contents.map((c, j) => ({
              content_id: c.contentId,
              is_mandatory: c.mandatory,
              sort_order: j + 1,
            })),
          })),
        }),
      ]);

      toast({ title: isCreating ? "Ruta creado" : "Ruta actualizado", description: "Los cambios se han guardado en la base de datos" });

      // Mantener al usuario en la edición de la ruta recién guardado.
      if (isCreating) {
        setEditingProgramId(programId);
        setIsCreating(false);
      }
      // Forzar re-hidratación desde DB para obtener IDs reales de sub-entidades nuevas.
      hydratedForIdRef.current = null;
      await queryClient.invalidateQueries({ queryKey: ['program-detail', programId] });
      queryClient.invalidateQueries({ queryKey: ['self-paced-stale-enrollees', programId] });
    } catch (e: any) {
      toast({ title: "Error al guardar", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    setEditingProgram(null);
    setEditingProgramId(null);
    setIsCreating(false);
  };

  // Workshop handlers
  const addWorkshop = () => {
    if (!editingProgram) return;
    const nextWeek = editingProgram.workshops.length + 1;
    const ws: Workshop = { id: Date.now().toString(), weekNumber: nextWeek, title: `Sesión en Vivo ${nextWeek}`, description: '', duration: '2 horas', materialLink: '', problemaConcreto: '', resultadoEsperado: '', tiempoEstimado: '45 minutos', tipAdicional: '', deliverableTools: [], requiresDeliverable: true, deliverableResourceUrl: null };
    setEditingProgram(prev => prev ? { ...prev, workshops: [...prev.workshops, ws] } : null);
  };

  const updateWorkshop = (id: string, updates: Partial<Workshop>) => {
    setEditingProgram(prev => prev ? { ...prev, workshops: prev.workshops.map(w => w.id === id ? { ...w, ...updates } : w) } : null);
  };

  // Catálogo de herramientas IA: una sola consulta cacheada para toda la página.
  // Solo alimenta el buscador de "Añadir herramienta"; las badges salen de lo guardado.
  const catalogToolNames = (aiToolsCatalog || []).map(t => t.name).sort((a, b) => a.localeCompare(b));

  const removeWorkshop = (id: string) => {
    setEditingProgram(prev => prev ? { ...prev, workshops: prev.workshops.filter(w => w.id !== id) } : null);
    toast({ title: 'Sesión en Vivo eliminada', description: 'Recuerda guardar los cambios para que se apliquen.' });
  };

  // Chapter handlers
  const addChapter = () => {
    if (!editingProgram) return;
    const nextOrder = editingProgram.chapters.length + 1;
    const ch: ProgramChapter = { id: Date.now().toString(), name: `Capítulo ${nextOrder}`, weekNumber: nextOrder, order: nextOrder, unlockConditions: [], contents: [] };
    setEditingProgram(prev => prev ? { ...prev, chapters: [...prev.chapters, ch] } : null);
  };

  const updateChapter = (id: string, updates: Partial<ProgramChapter>) => {
    setEditingProgram(prev => prev ? { ...prev, chapters: prev.chapters.map(ch => ch.id === id ? { ...ch, ...updates } : ch) } : null);
  };

  const removeChapter = (id: string) => {
    setEditingProgram(prev => prev ? { ...prev, chapters: prev.chapters.filter(ch => ch.id !== id) } : null);
    toast({ title: 'Capítulo eliminado', description: 'Recuerda guardar los cambios para que se apliquen.' });
  };

  const moveChapter = (id: string, direction: 'up' | 'down') => {
    if (!editingProgram) return;
    const sorted = [...editingProgram.chapters].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(ch => ch.id === id);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === sorted.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const tempOrder = sorted[idx].order;
    sorted[idx] = { ...sorted[idx], order: sorted[swapIdx].order };
    sorted[swapIdx] = { ...sorted[swapIdx], order: tempOrder };
    setEditingProgram(prev => prev ? { ...prev, chapters: sorted } : null);
  };

  const toggleChapterCondition = (chapterId: string, condition: UnlockCondition) => {
    setEditingProgram(prev => {
      if (!prev) return null;
      return {
        ...prev,
        chapters: prev.chapters.map(ch => {
          if (ch.id !== chapterId) return ch;
          const has = ch.unlockConditions.includes(condition);
          return { ...ch, unlockConditions: has ? ch.unlockConditions.filter(c => c !== condition) : [...ch.unlockConditions, condition] };
        }),
      };
    });
  };

  const addContentToChapter = (chapterId: string, content: { id: string; name: string; content_type: string | null; duration: string | null }) => {
    if (!editingProgram) return;
    const chapter = editingProgram.chapters.find(ch => ch.id === chapterId);
    if (!chapter) return;
    if (chapter.contents.find(c => c.contentId === content.id)) {
      toast({ title: "Ya agregado", description: "Este contenido ya está en este capítulo", variant: "destructive" });
      return;
    }
    const pc: ProgramContent = {
      id: Date.now().toString(),
      contentId: content.id,
      name: content.name,
      type: content.content_type || '',
      duration: content.duration || '',
      mandatory: false,
      order: chapter.contents.length + 1,
    };
    setEditingProgram(prev => prev ? {
      ...prev,
      chapters: prev.chapters.map(ch => ch.id === chapterId ? { ...ch, contents: [...ch.contents, pc] } : ch),
    } : null);
  };

  const toggleChapterContentMandatory = (chapterId: string, contentId: string) => {
    setEditingProgram(prev => prev ? {
      ...prev,
      chapters: prev.chapters.map(ch => ch.id === chapterId ? {
        ...ch, contents: ch.contents.map(c => c.id === contentId ? { ...c, mandatory: !c.mandatory } : c),
      } : ch),
    } : null);
  };

  const removeChapterContent = (chapterId: string, contentId: string) => {
    setEditingProgram(prev => prev ? {
      ...prev,
      chapters: prev.chapters.map(ch => ch.id === chapterId ? {
        ...ch, contents: ch.contents.filter(c => c.id !== contentId).map((c, i) => ({ ...c, order: i + 1 })),
      } : ch),
    } : null);
  };

  const moveChapterContent = (chapterId: string, contentId: string, direction: 'up' | 'down') => {
    if (!editingProgram) return;
    setEditingProgram(prev => {
      if (!prev) return null;
      return {
        ...prev,
        chapters: prev.chapters.map(ch => {
          if (ch.id !== chapterId) return ch;
          const sorted = [...ch.contents].sort((a, b) => a.order - b.order);
          const idx = sorted.findIndex(c => c.id === contentId);
          if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === sorted.length - 1)) return ch;
          const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
          const tempOrder = sorted[idx].order;
          sorted[idx] = { ...sorted[idx], order: sorted[swapIdx].order };
          sorted[swapIdx] = { ...sorted[swapIdx], order: tempOrder };
          return { ...ch, contents: sorted };
        }),
      };
    });
  };

  // (Old single-certificate handlers removed — replaced by multi-certificate editor)

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Activo</Badge>;
      case 'public': return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Público</Badge>;
      case 'inactive':
      case 'draft': return <Badge variant="secondary">Borrador</Badge>;
      case 'suspended':
      case 'archived': return <Badge variant="outline" className="text-muted-foreground">Archivado</Badge>;
      default: return null;
    }
  };

  const getCategoryLabel = (cat: string) => {
    return cat === 'specialized'
      ? <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">Especializado</Badge>
      : <Badge className="bg-primary/10 text-primary border-primary/20">Principal</Badge>;
  };


  // Computed
  const programs = dbPrograms || [];

  const isSelfPacedProgram = (p: any) => p.modality === 'self_paced';
  const isPublicProgram = (p: any) => p.status === 'public';
  const statsAll = programs.length;
  const statsGuidedPublic = programs.filter(p => !isSelfPacedProgram(p) && isPublicProgram(p)).length;
  const statsSelfPacedPublic = programs.filter(p => isSelfPacedProgram(p) && isPublicProgram(p)).length;
  const statsActive = programs.filter(p => p.status === 'active').length;
  const statsPublic = programs.filter(p => isPublicProgram(p)).length;

  const filteredPrograms = programs.filter((p: any) => {
    const term = listSearch.trim().toLowerCase();
    const matchesSearch = term === '' ||
      (p.name || '').toLowerCase().includes(term) ||
      (p.description || '').toLowerCase().includes(term);
    const modality = isSelfPacedProgram(p) ? 'self_paced' : 'cohort';
    const matchesModality = listModality === 'all' || modality === listModality;
    const matchesCategory = listCategory === 'all' || (p.category || 'general') === listCategory;
    const matchesStatus = listStatus === 'all' || (p.status || 'active') === listStatus;
    return matchesSearch && matchesModality && matchesCategory && matchesStatus;
  });
  const hasListFilters = listSearch !== '' || listModality !== 'all' || listCategory !== 'all' || listStatus !== 'all';

  const totalChapterContents = editingProgram ? editingProgram.chapters.reduce((sum, ch) => sum + ch.contents.length, 0) : 0;
  const allAssignedContentIds = editingProgram ? editingProgram.chapters.flatMap(ch => ch.contents.map(c => c.contentId)) : [];


  const PLANNING_STATUS_META: Record<string, { label: string; cls: string }> = {
    suggested: { label: 'Sugerido', cls: 'bg-muted text-muted-foreground border-border' },
    planning: { label: 'En Planificación', cls: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
    in_development: { label: 'En Desarrollo', cls: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
    in_review: { label: 'En Revisión', cls: 'bg-violet-500/10 text-violet-600 border-violet-500/20' },
    published: { label: 'Publicado', cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
  };
  const publishedCount = contentBankItems.filter((c: any) => (c.planning_status ?? 'published') === 'published').length;
  const filteredBankContent = contentBankItems.filter((c: any) => {
    const matchesSearch = contentBankSearch === '' || c.name.toLowerCase().includes(contentBankSearch.toLowerCase());
    const status = c.planning_status ?? 'published';
    if (status === 'archived') return false;
    const matchesStatus = contentBankStatusFilter === 'all' || status === contentBankStatusFilter;
    const matchesCategory = contentBankCategoryFilter === 'all'
      || (contentBankCategoryFilter === '_none_' ? !c.category : c.category === contentBankCategoryFilter);
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // ── LIST VIEW ──
  if (!editingProgram) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <GraduationCap className="w-7 h-7 text-primary" />
              Gestión de Rutas
            </h1>
            <p className="text-muted-foreground">Administra rutas y certificaciones de la plataforma</p>
            <p className="text-xs text-muted-foreground mt-1">Para ver el histórico de certificados emitidos, ve a Gestión de Cohortes → Certificados Generados.</p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleCreateNew}><Plus className="w-4 h-4 mr-2" />Nueva Ruta</Button>
        </div>

        {loadingPrograms ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Cargando rutas...</span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{statsAll}</p><p className="text-sm text-muted-foreground">Todas las rutas</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{statsGuidedPublic}</p><p className="text-sm text-muted-foreground">Con acompañamiento públicas</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{statsSelfPacedPublic}</p><p className="text-sm text-muted-foreground">Autoaprendizaje públicas</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{statsActive} / {statsPublic}</p><p className="text-sm text-muted-foreground">Rutas activas / públicas</p></CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-3 md:items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={listSearch}
                      onChange={(e) => setListSearch(e.target.value)}
                      placeholder="Buscar ruta por nombre o descripción..."
                      className="pl-9"
                    />
                  </div>
                  <Select value={listModality} onValueChange={(v) => setListModality(v as any)}>
                    <SelectTrigger className="w-full md:w-52"><SelectValue placeholder="Modalidad" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las modalidades</SelectItem>
                      <SelectItem value="cohort">Con acompañamiento</SelectItem>
                      <SelectItem value="self_paced">Autoaprendizaje</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={listCategory} onValueChange={(v) => setListCategory(v as any)}>
                    <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Categoría" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las categorías</SelectItem>
                      <SelectItem value="general">Principal</SelectItem>
                      <SelectItem value="specialized">Especializado</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={listStatus} onValueChange={(v) => setListStatus(v as any)}>
                    <SelectTrigger className="w-full md:w-44"><SelectValue placeholder="Estado" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      {statusOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {hasListFilters && (
                    <Button variant="ghost" onClick={() => { setListSearch(''); setListModality('all'); setListCategory('all'); setListStatus('all'); }}>
                      Limpiar
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{filteredPrograms.length} de {programs.length} rutas</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ruta / Certificación</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>Duración</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPrograms.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No hay rutas que coincidan con los filtros
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredPrograms.map((program) => (

                      <TableRow key={program.id} className="cursor-pointer" onClick={() => handleEdit(program.id)}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-8 rounded-full bg-gradient-to-b ${program.color || 'from-primary to-primary/70'}`} />
                            <div>
                              <p className="font-medium">{program.name}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">{(program.description || '').slice(0, 60)}...</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getCategoryLabel(program.category || 'general')}
                            {(program as any).modality === 'self_paced' && (
                              <Badge variant="secondary" className="gap-1">∞ Autoaprendizaje</Badge>
                            )}
                            {(program as any).is_featured && (
                              <Badge variant="outline" className="gap-1 border-amber-500/40 text-amber-600">
                                <Star className="w-3 h-3 fill-current" /> Destacada
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{program.duration || '-'}</TableCell>
                        <TableCell>{getStatusBadge(program.status || 'active')}</TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(program.id)} title="Editar"><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDuplicate(program.id)} title="Duplicar" disabled={duplicateProgram.isPending}><Copy className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => setShowDeleteDialog(program.id)} title="Eliminar"><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}

        {/* Delete Dialog */}
        <Dialog open={!!showDeleteDialog} onOpenChange={() => setShowDeleteDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>¿Eliminar ruta?</DialogTitle>
              <DialogDescription>Esta acción no se puede deshacer. Se eliminará la ruta, su contenido asociado y la configuración de certificado.</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowDeleteDialog(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={() => showDeleteDialog && handleDelete(showDeleteDialog)} disabled={deleteProgram.isPending}>
                {deleteProgram.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Eliminar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    );
  }

  // ── EDIT VIEW (Tabs) ──
  const isLoadingDetail = !isCreating && detailQuery.isLoading;
  const isSelfPaced = editingProgram?.modality === 'self_paced';

  if (isLoadingDetail) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Cargando ruta...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <GraduationCap className="w-7 h-7 text-primary" />
              {isCreating ? 'Nueva Ruta' : editingProgram.name}
            </h1>
            <p className="text-muted-foreground">
              {isCreating ? 'Configura todos los aspectos de la ruta' : 'Editar ruta, contenido, sesiones en vivo y certificado'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Select
            value={editingProgram.status}
            onValueChange={(value: 'active' | 'draft' | 'archived') => setEditingProgram(prev => prev ? { ...prev, status: value } : null)}
          >
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statusOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={isSelfPaced && !['info', 'content'].includes(activeTab) ? 'info' : activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${isSelfPaced ? 'grid-cols-2' : 'grid-cols-6'}`}>
          <TabsTrigger value="info" className="gap-2"><FileText className="w-4 h-4" />Información</TabsTrigger>
          {!isSelfPaced && <TabsTrigger value="workshops" className="gap-2"><Video className="w-4 h-4" />Sesiones en Vivo</TabsTrigger>}
          {!isSelfPaced && <TabsTrigger value="mentorships" className="gap-2"><GraduationCap className="w-4 h-4" />Mentorías</TabsTrigger>}
          <TabsTrigger value="content" className="gap-2"><BookOpen className="w-4 h-4" />Contenido</TabsTrigger>
          {!isSelfPaced && <TabsTrigger value="certificate" className="gap-2"><Award className="w-4 h-4" />Certificado</TabsTrigger>}
          {!isSelfPaced && <TabsTrigger value="interests" className="gap-2" disabled={isCreating}><Users className="w-4 h-4" />Interesados</TabsTrigger>}
        </TabsList>

        {/* ── TAB: Info ── */}
        <TabsContent value="info">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Datos de la Ruta</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nombre de la ruta *</Label>
                  <Input value={editingProgram.name} onChange={e => setEditingProgram(prev => prev ? { ...prev, name: e.target.value } : null)} placeholder="Ej: Profesional Líder IA" />
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea value={editingProgram.description} onChange={e => setEditingProgram(prev => prev ? { ...prev, description: e.target.value } : null)} rows={4} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nivel</Label>
                    <Select value={String(editingProgram.level)} onValueChange={v => setEditingProgram(prev => prev ? { ...prev, level: parseInt(v) } : null)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map(l => <SelectItem key={l} value={String(l)}>Nivel {l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Categoría</Label>
                    <Select value={editingProgram.category} onValueChange={(v: 'general' | 'specialized') => setEditingProgram(prev => prev ? { ...prev, category: v } : null)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="specialized">Especializada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Modalidad</Label>
                  <Select
                    value={editingProgram.modality}
                    onValueChange={(v: 'cohort' | 'self_paced') => setEditingProgram(prev => prev ? { ...prev, modality: v } : null)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cohort">Con acompañamiento (cohortes)</SelectItem>
                      <SelectItem value="self_paced">Autoaprendizaje (a tu ritmo)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {editingProgram.modality === 'self_paced'
                      ? 'Sin sesiones en vivo, mentorías ni certificado. Al guardar como Activa o Pública se habilita la inscripción continua.'
                      : 'Ruta con cohortes, sesiones en vivo, mentorías y certificado.'}
                  </p>
                  {editingProgram.modality === 'self_paced' && editingProgram.id && (
                    <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
                      <SelfPacedEnrolleeNotice programId={editingProgram.id} />
                      <p className="text-xs text-muted-foreground">
                        Cada inscrito conserva el temario que tenía la ruta al inscribirse. Los recursos nuevos no se les exigen; los que quites dejan de exigirse a todos.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!confirm('¿Aplicar el temario actual a todos los inscritos de esta ruta? Su progreso se recalculará con los obligatorios actuales.')) return;
                          const { data, error } = await supabase.rpc('resnapshot_self_paced_program', { _program_id: editingProgram.id });
                          if (error) toast({ title: 'No se pudo actualizar', description: error.message, variant: 'destructive' });
                          else toast({ title: 'Temario actualizado', description: `${data ?? 0} inscritos ahora usan el temario actual.` });
                          queryClient.invalidateQueries({ queryKey: ['self-paced-stale-enrollees', editingProgram.id] });
                        }}
                      >
                        Actualizar temario de los inscritos
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Configuración</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Clock className="w-4 h-4" />Duración</Label>
                  <Input value={editingProgram.duration} onChange={e => setEditingProgram(prev => prev ? { ...prev, duration: e.target.value } : null)} placeholder="Ej: 40 horas" />
                </div>
                <div className="space-y-2">
                  <Label>Imagen de portada (URL)</Label>
                  <Input value={editingProgram.imageUrl} onChange={e => setEditingProgram(prev => prev ? { ...prev, imageUrl: e.target.value } : null)} placeholder="https://..." />
                  <p className="text-xs text-muted-foreground">Opcional. Si se deja vacío se usa una imagen por defecto según el tipo de ruta.</p>
                  {editingProgram.imageUrl && (
                    <img src={editingProgram.imageUrl} alt="Portada de la ruta" loading="lazy" className="w-full max-w-xs aspect-video object-cover rounded-md border" />
                  )}
                </div>
                {!isSelfPaced && (
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Users className="w-4 h-4" />Máx. participantes</Label>
                      <Input type="number" min={1} value={editingProgram.maxParticipants} onChange={e => setEditingProgram(prev => prev ? { ...prev, maxParticipants: parseInt(e.target.value) || 1 } : null)} />
                    </div>
                  </div>
                )}

                <Separator />

                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" /> Ruta destacada
                    </Label>
                    <p className="text-xs text-muted-foreground">Aparece primero en el catálogo y se resalta con una estrella</p>
                  </div>
                  <Switch
                    checked={editingProgram.isFeatured}
                    onCheckedChange={(checked) => setEditingProgram(prev => prev ? { ...prev, isFeatured: checked } : null)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Desbloqueo secuencial de capítulos</Label>
                    <p className="text-xs text-muted-foreground">Los participantes deben completar ≥80% del contenido obligatorio del capítulo anterior para avanzar</p>
                  </div>
                  <Switch
                    checked={editingProgram.sequentialUnlock}
                    onCheckedChange={(checked) => setEditingProgram(prev => prev ? { ...prev, sequentialUnlock: checked } : null)}
                  />
                </div>

                <Separator />
                <div className="space-y-2">
                  <Label>Competencias clave</Label>
                  <div className="flex flex-wrap gap-2">
                    {editingProgram.skills.map((skill, i) => (
                      <Badge key={i} variant="secondary" className="gap-1">
                        {skill}
                        <button onClick={() => setEditingProgram(prev => prev ? { ...prev, skills: prev.skills.filter((_, idx) => idx !== i) } : null)} className="ml-1 hover:text-destructive">×</button>
                      </Badge>
                    ))}
                  </div>
                  <div className="relative">
                    <Input
                      placeholder="Buscar competencia…"
                      value={selectedSkill}
                      onChange={e => setSelectedSkill(e.target.value)}
                    />
                    {selectedSkill && (() => {
                      const matches = skillOptions.filter(s =>
                        !editingProgram.skills.includes(s) &&
                        s.toLowerCase().includes(selectedSkill.toLowerCase())
                      );
                      return matches.length > 0 ? (
                        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md max-h-48 overflow-y-auto">
                          {matches.map(s => (
                            <button
                              key={s}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                              onClick={() => {
                                setEditingProgram(prev => prev ? { ...prev, skills: [...prev.skills, s] } : null);
                                setSelectedSkill('');
                              }}
                            >{s}</button>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── TAB: Workshops ── */}
        <TabsContent value="workshops">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Sesiones en Vivo de la Ruta</CardTitle>
                <CardDescription>Configura las sesiones en vivo de la ruta</CardDescription>
              </div>
              <Button onClick={addWorkshop}><Plus className="w-4 h-4 mr-2" />Agregar Sesión en Vivo</Button>
            </CardHeader>
            <CardContent>
              {editingProgram.workshops.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No hay sesiones en vivo configuradas. Agrega una para comenzar.</p>
              ) : (
                <div className="space-y-4">
                  {editingProgram.workshops.sort((a, b) => a.weekNumber - b.weekNumber).map(ws => (
                    <Card key={ws.id} className="border-border/50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold text-sm shrink-0">S{ws.weekNumber}</div>
                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Título</Label>
                                <Input value={ws.title} onChange={e => updateWorkshop(ws.id, { title: e.target.value })} />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <Label className="text-xs">Semana</Label>
                                  <Input type="number" min={1} value={ws.weekNumber} onChange={e => updateWorkshop(ws.id, { weekNumber: parseInt(e.target.value) || 1 })} />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Duración</Label>
                                  <Input value={ws.duration} onChange={e => updateWorkshop(ws.id, { duration: e.target.value })} />
                                </div>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Descripción</Label>
                              <Input value={ws.description} onChange={e => updateWorkshop(ws.id, { description: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">📎 Link de Material (presentación)</Label>
                              <Input value={ws.materialLink} onChange={e => updateWorkshop(ws.id, { materialLink: e.target.value })} placeholder="https://docs.google.com/..." />
                            </div>

                            {/* Deliverable Instructions */}
                            <Separator className="my-1" />
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-semibold flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-primary" />
                                  Entregable de la Sesión en Vivo
                                </Label>
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs text-muted-foreground">Requiere entregable</Label>
                                  <Switch checked={ws.requiresDeliverable} onCheckedChange={v => updateWorkshop(ws.id, { requiresDeliverable: v })} />
                                </div>
                              </div>
                              {ws.requiresDeliverable && (
                              <Label className="text-sm font-semibold flex items-center gap-2">
                                Instrucciones del Entregable
                              </Label>
                              )}
                              {ws.requiresDeliverable && (
                              <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">🎯 Problema concreto</Label>
                                  <Textarea value={ws.problemaConcreto} onChange={e => updateWorkshop(ws.id, { problemaConcreto: e.target.value })} rows={2} placeholder="¿Qué problema específico debe resolver el participante?" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">✅ Resultado esperado</Label>
                                  <Textarea value={ws.resultadoEsperado} onChange={e => updateWorkshop(ws.id, { resultadoEsperado: e.target.value })} rows={2} placeholder="¿Qué debe entregar al finalizar?" />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">⏱️ Tiempo estimado</Label>
                                  <Input value={ws.tiempoEstimado} onChange={e => updateWorkshop(ws.id, { tiempoEstimado: e.target.value })} placeholder="Ej: 45 minutos" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">💡 Tip adicional</Label>
                                  <Input value={ws.tipAdicional} onChange={e => updateWorkshop(ws.id, { tipAdicional: e.target.value })} placeholder="Consejo para el participante" />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs">🛠️ Herramientas sugeridas</Label>
                                <ToolPicker
                                  selected={ws.deliverableTools}
                                  onChange={(tools) => updateWorkshop(ws.id, { deliverableTools: tools })}
                                  catalogNames={catalogToolNames}
                                  catalogLoading={aiToolsLoading}
                                />
                              </div>
                              <div className="space-y-2 border-t pt-3">
                                <Label className="text-xs">🧩 Recurso interactivo del entregable (HTML opcional)</Label>
                                {ws.deliverableResourceUrl ? (
                                  <div className="flex items-center gap-2 p-2 rounded border bg-muted/30">
                                    <FileText className="w-4 h-4 text-primary shrink-0" />
                                    <span className="text-xs flex-1 truncate text-muted-foreground">
                                      {ws.deliverableResourceUrl.split('/').pop()}
                                    </span>
                                    <Button type="button" variant="outline" size="sm" onClick={() => setPreviewResource({ url: ws.deliverableResourceUrl!, title: ws.title })}>
                                      <Eye className="w-3 h-3 mr-1" /> Ver
                                    </Button>
                                    <Button type="button" variant="ghost" size="sm" onClick={() => updateWorkshop(ws.id, { deliverableResourceUrl: null })}>
                                      <Trash2 className="w-3 h-3 text-destructive" />
                                    </Button>
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground">Sube un archivo .html que explique al participante cómo realizar el entregable. Se mostrará en una ventana grande dentro del calendario.</p>
                                )}
                                <input
                                  id={`deliverable-resource-${ws.id}`}
                                  type="file"
                                  accept=".html,text/html"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const isHtml = file.type === 'text/html' || file.name.toLowerCase().endsWith('.html');
                                    if (!isHtml) {
                                      toast({ title: 'Archivo no válido', description: 'Solo se permiten archivos .html', variant: 'destructive' });
                                      e.target.value = '';
                                      return;
                                    }
                                    if (file.size > 5 * 1024 * 1024) {
                                      toast({ title: 'Archivo demasiado grande', description: 'El máximo permitido es 5 MB.', variant: 'destructive' });
                                      e.target.value = '';
                                      return;
                                    }
                                    try {
                                      const path = `deliverable-resources/${ws.id}.html`;
                                      const { error: upErr } = await supabase.storage
                                        .from('content')
                                        .upload(path, file, { upsert: true, contentType: 'text/html', cacheControl: '60' });
                                      if (upErr) throw upErr;
                                      const { data } = supabase.storage.from('content').getPublicUrl(path);
                                      // Cache-bust so el iframe siempre vea la última versión
                                      const url = `${data.publicUrl}?v=${Date.now()}`;
                                      updateWorkshop(ws.id, { deliverableResourceUrl: url });
                                      toast({ title: 'Recurso subido', description: 'Recuerda guardar los cambios de la ruta.' });
                                    } catch (err: any) {
                                      toast({ title: 'Error al subir', description: err.message ?? 'No se pudo subir el archivo.', variant: 'destructive' });
                                    } finally {
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => document.getElementById(`deliverable-resource-${ws.id}`)?.click()}
                                >
                                  <Upload className="w-3 h-3 mr-1" />
                                  {ws.deliverableResourceUrl ? 'Reemplazar HTML' : 'Subir HTML'}
                                </Button>
                              </div>
                              </>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeWorkshop(ws.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB: Mentorships ── */}
        <TabsContent value="mentorships">
          <Card>
            <CardHeader>
              <CardTitle>Mentorías</CardTitle>
              <CardDescription>
                La ruta solo define si contempla mentorías. Cada cohorte crea y configura las suyas
                (nombre, semana, fecha, horario, mentor, enlace) de forma independiente: editarlas en una
                cohorte no afecta a la ruta ni a las demás cohortes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4 rounded-lg border border-border/50 p-4">
                <div className="space-y-1">
                  <Label htmlFor="has-mentorships" className="text-sm font-medium">¿Esta ruta incluye mentorías?</Label>
                  <p className="text-xs text-muted-foreground">
                    {editingProgram.hasMentorships
                      ? 'Sí: las cohortes de esta ruta podrán crear sus propias mentorías.'
                      : 'No: las cohortes de esta ruta no mostrarán la sección de mentorías.'}
                  </p>
                </div>
                <Switch
                  id="has-mentorships"
                  checked={editingProgram.hasMentorships}
                  onCheckedChange={v => setEditingProgram(prev => prev ? { ...prev, hasMentorships: v } : null)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB: Content (Chapters) ── */}
        <TabsContent value="content">
          <Card>
            <CardHeader className="sticky top-16 z-20 flex flex-row items-center justify-between gap-4 bg-card/95 backdrop-blur-sm border-b rounded-t-lg">
              <div>
                <CardTitle>Capítulos y Contenido</CardTitle>
                <CardDescription>Organiza el contenido de la ruta en capítulos semanales. Contenidos: {totalChapterContents} asignados.</CardDescription>
              </div>
              <Button onClick={addChapter} className="shrink-0"><Plus className="w-4 h-4 mr-2" />Agregar Capítulo</Button>
            </CardHeader>
            <CardContent>
              {editingProgram.chapters.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No hay capítulos. Agrega uno para organizar el contenido.</p>
              ) : (
                <div className="space-y-4">
                  {[...editingProgram.chapters].sort((a, b) => a.order - b.order).map((ch) => (
                    <Card key={ch.id} className="border-border/50">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col gap-0.5">
                            <button onClick={() => moveChapter(ch.id, 'up')} className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-4 h-4 rotate-90" /></button>
                            <button onClick={() => moveChapter(ch.id, 'down')} className="text-muted-foreground hover:text-foreground"><ChevronRight className="w-4 h-4 rotate-90" /></button>
                          </div>
                          <GripVertical className="w-4 h-4 text-muted-foreground" />
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Nombre del Capítulo</Label>
                              <Input value={ch.name} onChange={e => updateChapter(ch.id, { name: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Semana</Label>
                              <Input type="number" min={1} value={ch.weekNumber} onChange={e => updateChapter(ch.id, { weekNumber: parseInt(e.target.value) || 1 })} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Condiciones de desbloqueo</Label>
                              <div className="flex flex-wrap gap-1.5">
                                {(Object.keys(unlockConditionLabels) as UnlockCondition[]).map(cond => (
                                  <Badge key={cond} variant={ch.unlockConditions.includes(cond) ? 'default' : 'outline'}
                                    className="cursor-pointer text-xs"
                                    onClick={() => toggleChapterCondition(ch.id, cond)}
                                  >{unlockConditionLabels[cond]}</Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeChapter(ch.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        </div>

                        {/* Chapter contents */}
                        <div className="ml-12 space-y-2">
                          {ch.contents.length === 0 && (
                            <p className="text-xs text-muted-foreground italic">Sin contenido asignado</p>
                          )}
                          {ch.contents.sort((a, b) => a.order - b.order).map((pc, pcIdx) => (
                            <div key={pc.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded-md text-sm">
                              <div className="flex flex-col">
                                <Button variant="ghost" size="icon" className="h-5 w-5" disabled={pcIdx === 0} onClick={() => moveChapterContent(ch.id, pc.id, 'up')}>
                                  <ChevronLeft className="w-3 h-3 rotate-90" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-5 w-5" disabled={pcIdx === ch.contents.length - 1} onClick={() => moveChapterContent(ch.id, pc.id, 'down')}>
                                  <ChevronRight className="w-3 h-3 rotate-90" />
                                </Button>
                              </div>
                              <BookOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <span className="text-xs text-muted-foreground w-5 text-center">{pc.order}</span>
                              <span className="flex-1">{pc.name}</span>
                              <Badge variant="outline" className="text-xs">{pc.type}</Badge>
                              <span className="text-xs text-muted-foreground">{pc.duration}</span>
                              <div className="flex items-center gap-1">
                                <Switch checked={pc.mandatory} onCheckedChange={() => toggleChapterContentMandatory(ch.id, pc.id)} />
                                <span className="text-xs text-muted-foreground">{pc.mandatory ? 'Obligatorio' : 'Opcional'}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                title="Previsualizar"
                                onClick={() => {
                                  const item = contentBankItems.find((c: any) => c.id === pc.contentId);
                                  if (item) setPreviewBankItem(item);
                                }}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeChapterContent(ch.id, pc.id)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                            </div>
                          ))}
                          <Button variant="outline" size="sm" onClick={() => setAddingContentToChapter(ch.id)}><Plus className="w-3 h-3 mr-1" />Agregar Contenido</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add content dialog — uses real content bank */}
          <Dialog open={!!addingContentToChapter} onOpenChange={() => { setAddingContentToChapter(null); setContentBankSearch(''); setContentBankCategoryFilter('all'); }}>
            <DialogContent className="max-w-3xl w-[95vw] max-h-[85vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Agregar Contenido del Banco</DialogTitle>
                <DialogDescription>
                  Selecciona contenido del banco para agregar al capítulo.{' '}
                  <span className="font-medium">{contentBankItems.length} disponibles</span>
                  {' · '}
                  <span className="text-emerald-600">{publishedCount} publicados</span>
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col flex-1 min-h-0 space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar contenido..." value={contentBankSearch} onChange={e => setContentBankSearch(e.target.value)} className="pl-9" />
                  </div>
                  <Select value={contentBankCategoryFilter} onValueChange={setContentBankCategoryFilter}>
                    <SelectTrigger className="w-full sm:w-48 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las categorías</SelectItem>
                      {contentCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                      <SelectItem value="_none_">Sin categoría</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={contentBankStatusFilter} onValueChange={setContentBankStatusFilter}>
                    <SelectTrigger className="w-full sm:w-48 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      <SelectItem value="published">Solo Publicado</SelectItem>
                      <SelectItem value="in_review">En Revisión</SelectItem>
                      <SelectItem value="in_development">En Desarrollo</SelectItem>
                      <SelectItem value="planning">En Planificación</SelectItem>
                      <SelectItem value="suggested">Sugerido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto space-y-1 pr-1 border rounded-md p-2">
                  {contentBankQuery.isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredBankContent.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-4">No se encontró contenido</p>
                  ) : (
                    filteredBankContent.map((c: any) => {
                      const isAssigned = allAssignedContentIds.includes(c.id);
                      const status = c.planning_status ?? 'published';
                      const meta = PLANNING_STATUS_META[status] ?? PLANNING_STATUS_META.published;
                      return (
                        <div key={c.id}
                          className={cn("flex items-start gap-2 p-2 rounded-md cursor-pointer hover:bg-accent/50 text-sm", isAssigned && "opacity-50 cursor-not-allowed")}
                          onClick={() => {
                            if (isAssigned || !addingContentToChapter) return;
                            if (status !== 'published') {
                              toast({
                                title: 'Contenido no publicado',
                                description: `"${c.name}" está en estado "${meta.label}". No será visible para los participantes hasta que cambies su estado a Publicado.`,
                                variant: 'destructive',
                              });
                            }
                            addContentToChapter(addingContentToChapter, c);
                          }}
                        >
                          <BookOpen className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{c.name}</div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              <Badge variant="outline" className={cn("text-xs", meta.cls)}>{meta.label}</Badge>
                              <Badge variant="outline" className="text-xs">{c.content_type || 'Sin tipo'}</Badge>
                              {c.duration && <span className="text-xs text-muted-foreground">· {c.duration}</span>}
                              {c.category && <span className="text-xs text-muted-foreground truncate">· {c.category}</span>}
                              {isAssigned && <Badge variant="secondary" className="text-xs">Ya asignado</Badge>}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0 h-7 w-7"
                            title="Previsualizar"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewBankItem(c);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <ContentPlayerModal
            isOpen={!!previewBankItem}
            onClose={() => setPreviewBankItem(null)}
            lessonIndex={0}
            onComplete={() => {}}
            lesson={previewBankItem ? {
              id: previewBankItem.id,
              title: previewBankItem.name,
              duration: previewBankItem.duration ?? '',
              description: previewBankItem.description ?? '',
              type: CONTENT_TYPE_MAP[previewBankItem.content_type ?? ''] ?? 'reading',
              completed: false,
              thumbnail: previewBankItem.thumbnail_url ?? '',
              vimeo_player_url: previewBankItem.vimeo_player_url,
              html_content: previewBankItem.html_content ?? null,
              url: previewBankItem.url,
              thumbnail_url: previewBankItem.thumbnail_url,
            } : null}
          />
        </TabsContent>

        {/* ── TAB: Certificate ── */}
        <TabsContent value="certificate">
          <ProgramCertificateTab programId={editingProgramId} />
        </TabsContent>

        {/* ── TAB: Interesados (solo rutas con acompañamiento) ── */}
        {!isSelfPaced && !isCreating && editingProgramId && (
          <TabsContent value="interests">
            <RouteInterestsTab programId={editingProgramId} programName={editingProgram.name} />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!previewResource} onOpenChange={(open) => !open && setPreviewResource(null)}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b border-border flex-shrink-0">
            <DialogTitle className="text-base">Recurso interactivo · {previewResource?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-muted/20">
            {previewResource && previewHtml === null && (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Cargando recurso…
              </div>
            )}
            {previewResource && previewHtml !== null && (
              <iframe
                srcDoc={previewHtml}
                sandbox="allow-scripts allow-forms allow-popups"
                className="w-full h-full border-0"
                title={previewResource.title}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
